import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Activity,
  AudioLines,
  BadgeQuestionMark,
  Bell,
  Brain,
  Calendar,
  ChartNoAxesCombined,
  ChartPie,
  CircleDollarSign,
  CirclePercent,
  CirclePlay,
  ContactRound,
  FileText,
  Handshake,
  Image,
  Lightbulb,
  ListFilter,
  Megaphone,
  MessageCircle,
  NotebookPen,
  Search,
  Shield,
  ShieldCheck,
  ShoppingBag,
  Star,
  Tag,
  UserPlus,
  Zap,
} from 'lucide-react';

import { getTypographyClassaname } from '@components/typography';
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/ui/tooltip';

import { cn } from '@src/lib/utils';

import { Marquee } from '@/components/magicui/marquee';

import LogoImg from '@assets/logoRoundedComplete.avif';

const iconSize = 34;
const iconStroke = 2;

export function Iconify({ icon }: { icon: { id: string; icon: React.ReactNode; label: string } }) {
  return (
    <Tooltip>
      <TooltipTrigger aria-label={icon.label}>
        <div className="cursor-pointer font-bold bg-zinc-200 rounded-3xl w-[90px] h-[90px] text-zinc-500 flex justify-center items-center border-b-4 border-zinc-500">
          {icon.icon}
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <p className={cn(getTypographyClassaname('base', 'center'), 'text-white')}>{icon.label}</p>
      </TooltipContent>
    </Tooltip>
  );
}

export function MarqueeView() {
  const { t } = useTranslation();

  const icons = [
    {
      id: 'searches',
      icon: <Search size={iconSize} strokeWidth={iconStroke} />,
      label: t('Busca en tus chats'),
    },
    {
      id: 'books',
      icon: <Calendar size={iconSize} strokeWidth={iconStroke} />,
      label: t('Agendamos'),
    },
    {
      id: 'reminds',
      icon: <Bell size={iconSize} strokeWidth={iconStroke} />,
      label: t('Enviamos recordatorios'),
    },
    {
      id: 'bills',
      icon: <CircleDollarSign size={iconSize} strokeWidth={iconStroke} />,
      label: t('Creamos links de pago'),
    },
    {
      id: 'increasessales',
      icon: <ChartNoAxesCombined size={iconSize} strokeWidth={iconStroke} />,
      label: t('Aumentamos las ventas'),
    },
    {
      id: 'answers',
      icon: <BadgeQuestionMark size={iconSize} strokeWidth={iconStroke} />,
      label: t('Respondemos preguntas'),
    },
    {
      id: 'chats',
      icon: <MessageCircle size={iconSize} strokeWidth={iconStroke} />,
      label: t('Enviamos mensajes'),
    },
    {
      id: 'advertises',
      icon: <Megaphone size={iconSize} strokeWidth={iconStroke} />,
      label: t('Hacemos publicidad'),
    },
    {
      id: 'discounts',
      icon: <CirclePercent size={iconSize} strokeWidth={iconStroke} />,
      label: t('Ofrecemos descuentos'),
    },
    {
      id: 'sendsImages',
      icon: <Image size={iconSize} strokeWidth={iconStroke} />,
      label: t('Enviamos imágenes'),
    },
    {
      id: 'sendsVideos',
      icon: <CirclePlay size={iconSize} strokeWidth={iconStroke} />,
      label: t('Enviamos videos'),
    },
    {
      id: 'sendsCatalog',
      icon: <FileText size={iconSize} strokeWidth={iconStroke} />,
      label: t('Enviamos tu catálogo'),
    },
    {
      id: 'sendsOrders',
      icon: <ShoppingBag size={iconSize} strokeWidth={iconStroke} />,
      label: t('Hacemos envíos'),
    },
    {
      id: 'tags',
      icon: <Tag size={iconSize} strokeWidth={iconStroke} />,
      label: t('Categorizamos tus clientes'),
    },
    {
      id: 'isSecure',
      icon: <Shield size={iconSize} strokeWidth={iconStroke} />,
      label: t('Somos seguros'),
    },
    {
      id: 'isScure2',
      icon: <ShieldCheck size={iconSize} strokeWidth={iconStroke} />,
      label: t('Somos seguros'),
    },
    {
      id: 'followsUp',
      icon: <Activity size={iconSize} strokeWidth={iconStroke} />,
      label: t('Hacemos seguimiento'),
    },
    {
      id: 'recognizesAudio',
      icon: <AudioLines size={iconSize} strokeWidth={iconStroke} />,
      label: t('Reconocemos audios'),
    },
    {
      id: 'isSmart',
      icon: <Brain size={iconSize} strokeWidth={iconStroke} />,
      label: t('Somos inteligentes'),
    },
    {
      id: 'generateLeads',
      icon: <UserPlus size={iconSize} strokeWidth={iconStroke} />,
      label: t('Generamos leads'),
    },
    {
      id: 'negotiates',
      icon: <Handshake size={iconSize} strokeWidth={iconStroke} />,
      label: t('Negociamos'),
    },
    {
      id: 'keepsContacts',
      icon: <ContactRound size={iconSize} strokeWidth={iconStroke} />,
      label: t('Guardamos contactos'),
    },
    {
      id: 'segments',
      icon: <ChartPie size={iconSize} strokeWidth={iconStroke} />,
      label: t('Segmentamos usuarios'),
    },
    {
      id: 'thinks',
      icon: <Lightbulb size={iconSize} strokeWidth={iconStroke} />,
      label: t('Somos proactivos'),
    },
    {
      id: 'notes',
      icon: <NotebookPen size={iconSize} strokeWidth={iconStroke} />,
      label: t('Tomamos notas'),
    },
    {
      id: 'isFast',
      icon: <Zap size={iconSize} strokeWidth={iconStroke} />,
      label: t('Somos rápidos'),
    },
    {
      id: 'favorites',
      icon: <Star size={iconSize} strokeWidth={iconStroke} />,
      label: t('Rankeamos usuarios'),
    },
    {
      id: 'filters',
      icon: <ListFilter size={iconSize} strokeWidth={iconStroke} />,
      label: t('Filtramos usuarios'),
    },
  ];

  const iconsSliceLength = icons.length / 4;

  const firstRow = icons.slice(0, iconsSliceLength);
  const secondRow = icons.slice(iconsSliceLength, iconsSliceLength * 2);
  const thirdRow = icons.slice(iconsSliceLength * 2, iconsSliceLength * 3);
  const fourthRow = icons.slice(iconsSliceLength * 3);

  return (
    <div className="relative flex w-full h-[500px] flex-col items-center justify-center overflow-hidden">
      <Marquee className="[--duration:35s]">
        {firstRow.map((icon) => (
          <Iconify key={icon.id} icon={icon}></Iconify>
        ))}
      </Marquee>
      <Marquee reverse className="[--duration:35s]">
        {secondRow.map((icon) => (
          <Iconify key={icon.id} icon={icon}></Iconify>
        ))}
      </Marquee>
      <Marquee className="[--duration:35s]">
        {thirdRow.map((icon) => (
          <Iconify key={icon.id} icon={icon}></Iconify>
        ))}
      </Marquee>
      <Marquee reverse className="[--duration:35s]">
        {fourthRow.map((icon) => (
          <Iconify key={icon.id} icon={icon}></Iconify>
        ))}
      </Marquee>

      <a
        href="https://w.app/rzv1fu"
        target="_blank"
        rel="noreferrer"
        aria-label="Contact Closer"
        className="absolute shadow-2xl rounded-4xl shadow-black no-underline cursor-pointer"
      >
        <img loading="lazy" className="w-[110px] h-[110px] rounded-4xl" src={LogoImg} alt="Closer logo" />
      </a>

      {/* Radial gradient overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at center, transparent 60%, white 80%)',
        }}
      ></div>
    </div>
  );
}
